/**
 * Every keyboard shortcut in the product, declared once.
 *
 * There used to be three lists: a `SHORTCUTS` const on the Settings page, a
 * `shortcuts` array inside KeyboardShortcuts.tsx, and the hand-written rows of
 * the `?` help modal. They disagreed. Settings advertised Ctrl+N "Create new
 * ticket", Ctrl+Shift+N and Ctrl+/ — none of which had a listener anywhere —
 * while hiding five navigation combos that did work, and labelled G A as
 * "Assets" when the handler went to /admin.
 *
 * Ctrl+N was the reported bug (TC_56) and could never have worked: the browser
 * reserves it for a new window and a page cannot intercept it. Ctrl+Shift+N
 * (new incognito window) is reserved the same way. Both are gone rather than
 * fixed, replaced by sequences the page is actually allowed to receive.
 *
 * `action` is DATA, not a closure, so this file stays free of React and can be
 * imported by a page, a modal and a key handler alike. Whoever handles a
 * shortcut interprets its action; whoever displays one only needs `keys` and
 * `label`.
 */

export type ShortcutAction =
  /** Go somewhere. `to` may carry ?action=… , which the target page consumes. */
  | { type: 'navigate'; to: string }
  /** Open the global search overlay. */
  | { type: 'openSearch' }
  /** Toggle the Quick Actions panel. */
  | { type: 'openQuickActions' }
  /** Toggle this help panel. */
  | { type: 'toggleHelp' }
  /**
   * Dismiss whatever is on top. Listed so the reference is complete, but NOT
   * handled centrally: each overlay closes itself (GlobalSearch, QuickActions,
   * ContextMenu and every Headless UI Dialog). A global Esc handler would be
   * guessing which one the user meant.
   */
  | { type: 'dismissTopmost' };

export interface Shortcut {
  /** As displayed. A two-letter entry with no modifier is a SEQUENCE: press
   *  them one after the other, not together. */
  keys: string[];
  label: string;
  action: ShortcutAction;
  /** The permission the destination's ACTION needs on the server, beyond
   *  opening the page (Create WO needs maintenance:create, not just the
   *  maintenance list). Opening the page itself is checked against the route
   *  map — see shortcutsFor in config/shortcutAccess. */
  permission?: string;
}

/** Sequence prefixes: press this, then the next key within a second. */
export const SEQUENCE_PREFIXES = ['G', 'C'] as const;

export const SHORTCUTS: Shortcut[] = [
  // ---- Actions (C = create) --------------------------------------------
  // C T / C W replace Ctrl+N / Ctrl+Shift+N. Sequences rather than chords
  // because the reserved combos are undeliverable, and because the existing
  // G-prefix mechanism already works and needed no new concept.
  { keys: ['C', 'T'], label: 'Create new ticket', action: { type: 'navigate', to: '/helpdesk?action=new' },
    permission: 'helpdesk:create' },
  { keys: ['C', 'W'], label: 'Create new work order', action: { type: 'navigate', to: '/maintenance?action=new' },
    permission: 'maintenance:create' },

  // ---- Global ----------------------------------------------------------
  { keys: ['Ctrl/Cmd', 'K'], label: 'Open search', action: { type: 'openSearch' } },
  { keys: ['/'], label: 'Focus search', action: { type: 'openSearch' } },
  { keys: ['Q'], label: 'Quick Actions', action: { type: 'openQuickActions' } },
  { keys: ['?'], label: 'Show keyboard shortcuts', action: { type: 'toggleHelp' } },
  { keys: ['Esc'], label: 'Close modal / panel', action: { type: 'dismissTopmost' } },

  // ---- Navigation (G = go) ---------------------------------------------
  { keys: ['G', 'D'], label: 'Go to Dashboard', action: { type: 'navigate', to: '/dashboard' } },
  { keys: ['G', 'S'], label: 'Go to Spaces', action: { type: 'navigate', to: '/spaces' } },
  { keys: ['G', 'H'], label: 'Go to Helpdesk', action: { type: 'navigate', to: '/helpdesk' } },
  { keys: ['G', 'M'], label: 'Go to Maintenance', action: { type: 'navigate', to: '/maintenance' } },
  // /assets, not /admin. The two lists disagreed and the code was the wrong one.
  { keys: ['G', 'A'], label: 'Go to Assets', action: { type: 'navigate', to: '/assets' } },
  { keys: ['G', 'V'], label: 'Go to Visitors', action: { type: 'navigate', to: '/visitors' } },
  { keys: ['G', 'L'], label: 'Go to Leases', action: { type: 'navigate', to: '/leases' } },
  { keys: ['G', 'E'], label: 'Go to Energy', action: { type: 'navigate', to: '/energy' } },
  { keys: ['G', 'C'], label: 'Go to Compliance', action: { type: 'navigate', to: '/compliance' } },
  { keys: ['G', 'P'], label: 'Go to Profile', action: { type: 'navigate', to: '/profile' } },
];

/** A two-key entry with no modifier, e.g. G then D. */
export function isSequence(s: Shortcut): boolean {
  return s.keys.length === 2 && (SEQUENCE_PREFIXES as readonly string[]).includes(s.keys[0] ?? '');
}

/** Held together, e.g. Ctrl/Cmd + K. Handled in AppLayout, not the key panel. */
export function isChord(s: Shortcut): boolean {
  return s.keys.length > 1 && !isSequence(s);
}

export const SEQUENCE_SHORTCUTS = SHORTCUTS.filter(isSequence);

/** Grouped for display. Both the Settings page and the `?` modal use this, so
 *  the two cannot show different things. */
export const SHORTCUT_GROUPS: { title: string; items: Shortcut[] }[] = [
  { title: 'Actions', items: SHORTCUTS.filter((s) => s.keys[0] === 'C') },
  { title: 'Global', items: SHORTCUTS.filter((s) => !isSequence(s)) },
  { title: 'Navigation (G + key)', items: SHORTCUTS.filter((s) => s.keys[0] === 'G') },
];
